import type { IChartApi, ISeriesApi, Time } from "lightweight-charts";
import { sameVwapCurvePoints } from "./vwapCurves.ts";
import { shiftLogicalWindow } from "./chartViewport.ts";
import { candleIsVisible } from "./tide.ts";

// Vite aliases the package's public entry to this wrapper. Import the concrete vendor module here
// so the wrapper can register chart ownership without recursing through its own alias.
// @ts-expect-error lightweight-charts publishes declarations for its public entry, not dist files.
import * as vendor from "../../node_modules/lightweight-charts/dist/lightweight-charts.production.mjs";

// @ts-expect-error See the import above; Vite needs the runtime re-exports while TypeScript reads
// the public package declarations at every unchanged lightweight-charts import site.
export * from "../../node_modules/lightweight-charts/dist/lightweight-charts.production.mjs";

const charts = new WeakMap<HTMLElement, IChartApi>();
// A native gesture owns its chart until a null-time callback or host departure.
const cursorPublishers = new WeakSet<IChartApi>();
const requestedCursors = new WeakMap<IChartApi, { tSec: number; price: number }>();
const syntheticCursors = new WeakSet<IChartApi>();
const departingCursors = new WeakSet<IChartApi>();
const cursorTeardowns = new WeakMap<IChartApi, () => void>();
interface VwapSeriesState {
  series: ISeriesApi<"Line">;
  points: ReadonlyArray<{ t: number; value: number | null }>;
}
const vwapCurveSeries = new WeakMap<HTMLElement, Map<string, VwapSeriesState>>();

export type VwapCurveSyncPrerequisite = "chart host" | "native chart" | "price pane" | "curve color";
export type VwapCurveSyncRefusal = { prerequisite: VwapCurveSyncPrerequisite };

/** Register the child-owned chart before a parent overlay attempts its first frame sync. */
export function registerNativeChart(host: HTMLElement, chart: IChartApi): () => void {
  charts.set(host, chart);
  return () => {
    if (charts.get(host) !== chart) return;
    cursorTeardowns.get(chart)?.();
    requestedCursors.delete(chart);
    syntheticCursors.delete(chart);
    cursorPublishers.delete(chart);
    charts.delete(host);
    vwapCurveSeries.delete(host);
  };
}

export const createChart: typeof import("lightweight-charts").createChart = (container, options) => {
  const host = typeof container === "string" ? document.getElementById(container) : container;
  const chart = vendor.createChart(container, options) as IChartApi;
  if (host) {
    const unregister = registerNativeChart(host, chart);
    const remove = chart.remove.bind(chart);
    chart.remove = () => {
      unregister();
      remove();
    };
  }
  return chart;
};

export interface VisibleTimeRange {
  from: Time;
  to: Time;
}

function chartInside(root: ParentNode | null): IChartApi | null {
  const host = root?.querySelector<HTMLElement>(".host") ?? null;
  return host === null ? null : charts.get(host) ?? null;
}

/** Pane zero's native candle series owns the price scale and the chart's time-axis label. */
function priceSeriesInside(root: ParentNode | null): ISeriesApi<"Candlestick"> | null {
  const series = chartInside(root)?.panes()[0]?.getSeries()
    .find((candidate) => candidate.seriesType() === "Candlestick");
  return (series as ISeriesApi<"Candlestick"> | undefined) ?? null;
}

export function setChartTimeCursor(root: ParentNode | null, tSec: number, price: number): boolean {
  if (!Number.isFinite(tSec)) throw new TypeError("chart cursor time must be finite");
  if (!Number.isFinite(price)) throw new TypeError("chart cursor price must be finite");
  const chart = chartInside(root);
  const series = priceSeriesInside(root);
  if (chart === null || series === null) return false;
  if (cursorPublishers.has(chart)) return false;
  requestedCursors.set(chart, { tSec, price });
  if (departingCursors.has(chart)) return false;
  const scale = chart.timeScale();
  const range = scale.getVisibleLogicalRange();
  const index = scale.timeToIndex(tSec as Time, false);
  if (range === null || index === null || !candleIsVisible(Number(index), Number(range.from), Number(range.to))) {
    if (syntheticCursors.has(chart)) {
      syntheticCursors.delete(chart);
      chart.clearCrosshairPosition();
    }
    return false;
  }
  syntheticCursors.add(chart);
  chart.setCrosshairPosition(price, tSec as Time, series);
  return true;
}

export function clearChartTimeCursor(root: ParentNode | null): void {
  const chart = chartInside(root);
  if (chart === null || cursorPublishers.has(chart)) return;
  requestedCursors.delete(chart);
  if (!syntheticCursors.has(chart)) return;
  syntheticCursors.delete(chart);
  chart.clearCrosshairPosition();
}

/** Native ownership admits lifecycle updates; unowned source-free callbacks are synthetic echoes. */
export function onChartUserCrosshair(root: ParentNode | null, cb: (tSec: number | null) => void): (() => void) | null {
  const chart = chartInside(root);
  const host = hostInside(root);
  if (chart === null || host === null) return null;
  const listener: Parameters<IChartApi["subscribeCrosshairMove"]>[0] = (event) => {
    // LWC 5.2.1 updateCrosshair reports the saved pixel after autoscale changes.
    // Synthetic placement skips the vendor event; remapping here never publishes.
    if (!event.sourceEvent && !cursorPublishers.has(chart) && event.point && syntheticCursors.has(chart)) {
      const request = requestedCursors.get(chart);
      const y = request === undefined ? null : priceSeriesInside(root)?.priceToCoordinate(request.price);
      if (y != null && y !== event.point.y) remap();
    }
    if (!event.sourceEvent && !cursorPublishers.has(chart)) return;
    requestedCursors.delete(chart);
    syntheticCursors.delete(chart);
    const time = typeof event.time === "number" && Number.isFinite(event.time) ? event.time : null;
    if (time === null) cursorPublishers.delete(chart);
    else cursorPublishers.add(chart);
    cb(time);
  };
  let departureTask: ReturnType<typeof setTimeout> | null = null;
  // Release now, but defer follower ink past LWC's compatibility mouseleave clear.
  // Either LWC's null-time callback or host departure ends ownership, exactly once.
  const leave = () => {
    if (!cursorPublishers.has(chart)) return;
    cursorPublishers.delete(chart);
    departingCursors.add(chart);
    departureTask = setTimeout(() => {
      departureTask = null;
      departingCursors.delete(chart);
      if (!stopped) remap();
    }, 0);
    cb(null);
  };
  const scale = chart.timeScale();
  const remap = () => {
    const applied = requestedCursors.get(chart);
    if (applied !== undefined) setChartTimeCursor(root, applied.tSec, applied.price);
  };
  // LWC saves pixels: re-place from time after its own range/size update.
  scale.subscribeVisibleLogicalRangeChange(remap);
  scale.subscribeSizeChange(remap);
  // LWC 5.2.1 exposes the pane element, but no public pane-size subscription.
  const pricePane = chart.panes()[0]?.getHTMLElement();
  const paneResize = pricePane == null ? null : new ResizeObserver(() => remap());
  if (pricePane != null) paneResize!.observe(pricePane);
  chart.subscribeCrosshairMove(listener);
  host.addEventListener("pointerleave", leave);
  let stopped = false;
  const stop = () => {
    if (stopped) return;
    stopped = true;
    if (departureTask !== null) clearTimeout(departureTask);
    departingCursors.delete(chart);
    paneResize?.disconnect();
    scale.unsubscribeVisibleLogicalRangeChange(remap);
    scale.unsubscribeSizeChange(remap);
    chart.unsubscribeCrosshairMove(listener);
    host.removeEventListener("pointerleave", leave);
    const owned = cursorPublishers.delete(chart);
    requestedCursors.delete(chart);
    syntheticCursors.delete(chart);
    cursorTeardowns.delete(chart);
    if (owned) cb(null);
  };
  cursorTeardowns.set(chart, stop);
  return stop;
}

function hostInside(root: ParentNode | null): HTMLElement | null {
  return root?.querySelector<HTMLElement>(".host") ?? null;
}

export interface ChartHostObserverConstructors {
  MutationObserver: typeof MutationObserver;
  ResizeObserver: typeof ResizeObserver;
}

/**
 * Signal only when the child chart host exists and owns a positive rendered box.
 *
 * MutationObserver covers the loading branch inserting `.host`; ResizeObserver covers a host that
 * exists while maximize hides its cell and later becomes measurable again. The observer remains
 * live until its caller stops it, so a zero box is never mistaken for permanent absence.
 */
export function observePositiveChartHost(
  root: ParentNode,
  onReady: () => void,
  constructors: ChartHostObserverConstructors = {
    MutationObserver: globalThis.MutationObserver,
    ResizeObserver: globalThis.ResizeObserver,
  },
): () => void {
  if (typeof constructors.MutationObserver !== "function" || typeof constructors.ResizeObserver !== "function") {
    throw new TypeError("VWAP chart host readiness: MutationObserver and ResizeObserver are required");
  }
  let stopped = false;
  let observedHost: HTMLElement | null = null;
  let resizeObserver: ResizeObserver | null = null;

  const check = (): void => {
    if (stopped) return;
    const nextHost = hostInside(root);
    if (nextHost !== observedHost) {
      resizeObserver?.disconnect();
      resizeObserver = null;
      observedHost = nextHost;
      if (observedHost !== null) {
        resizeObserver = new constructors.ResizeObserver(check);
        resizeObserver.observe(observedHost);
      }
    }
    if (observedHost === null) return;
    const box = observedHost.getBoundingClientRect();
    if (Number.isFinite(box.width) && box.width > 0 && Number.isFinite(box.height) && box.height > 0) {
      onReady();
    }
  };

  const mutationObserver = new constructors.MutationObserver(check);
  mutationObserver.observe(root, { childList: true, subtree: true });
  check();
  return () => {
    stopped = true;
    mutationObserver.disconnect();
    resizeObserver?.disconnect();
    resizeObserver = null;
    observedHost = null;
  };
}

export interface NativeVwapCurve {
  id: string;
  color: string;
  points: ReadonlyArray<{ t: number; value: number | null }>;
}

/** Draw curves on pane 0's native price scale so resize/maximize and autoscale remain exact. */
export function syncVwapCurveSeries(
  root: ParentNode | null,
  curves: readonly NativeVwapCurve[],
  retainedIds: readonly string[],
  onRefusal?: (refusal: VwapCurveSyncRefusal) => void,
): boolean {
  const refuse = (prerequisite: VwapCurveSyncPrerequisite): false => {
    onRefusal?.({ prerequisite });
    return false;
  };
  const host = hostInside(root);
  const chart = host === null ? null : charts.get(host) ?? null;
  if (host === null) return refuse("chart host");
  if (chart === null) return refuse("native chart");
  if (chart.panes()[0] === undefined) return refuse("price pane");
  if (curves.some((curve) => curve.color.length === 0)) return refuse("curve color");
  const existing = vwapCurveSeries.get(host) ?? new Map<string, VwapSeriesState>();
  const wanted = new Set(curves.map((curve) => curve.id));
  const retained = new Set(retainedIds);
  for (const [id, state] of existing) {
    if (wanted.has(id)) continue;
    if (retained.has(id)) {
      // Keep the native owner stable for a toggle-off; rebuilding it can move a panned window.
      if (state.points.length) state.series.setData([]);
      state.points = [];
    } else {
      // A deleted pulled anchor is not a toggle. Release the native owner instead of leaking one
      // empty LineSeries for every create/remove cycle.
      chart.removeSeries(state.series);
      existing.delete(id);
    }
  }
  for (const curve of curves) {
    let state = existing.get(curve.id);
    if (!state) {
      const series = chart.addSeries(vendor.LineSeries, {
        // ORDER 41-B: the id remains in the native/data readback, never as permanent canvas ink.
        title: "",
        color: curve.color,
        lineWidth: 1,
        lineStyle: vendor.LineStyle.Solid,
        priceLineVisible: false,
        lastValueVisible: false,
        crosshairMarkerVisible: false,
      }, 0) as ISeriesApi<"Line">;
      state = { series, points: [] };
      existing.set(curve.id, state);
    } else {
      state.series.applyOptions({ title: "", color: curve.color, lineWidth: 1, lineStyle: vendor.LineStyle.Solid });
    }
    const unchanged = sameVwapCurvePoints(state.points, curve.points);
    if (!unchanged) {
      state.series.setData(curve.points.map((point) => point.value === null
        ? { time: Math.floor(point.t / 1_000) as Time }
        : { time: Math.floor(point.t / 1_000) as Time, value: point.value }));
      state.points = curve.points;
    }
  }
  vwapCurveSeries.set(host, existing);
  host.dataset.vwapCurveSeries = JSON.stringify([...wanted]);
  host.dataset.vwapCurveOwners = JSON.stringify([...existing.keys()]);
  host.dataset.vwapCurveStyles = JSON.stringify(curves.map(({ id, color }) => ({ id, color })));
  host.dataset.vwapCurveTitles = JSON.stringify([...existing].map(([id, state]) => ({
    id,
    title: state.series.options().title ?? "",
  })));
  host.dataset.vwapCurveSeriesData = JSON.stringify([...existing].map(([id, state]) => ({
    id,
    points: state.points.filter((point) => point.value !== null).length,
  })));
  return true;
}

/** Convert a viewport click through LWC's actual time scale (including whitespace/gaps and gutters). */
export function chartTimeAtClientX(root: ParentNode | null, clientX: number): number | null {
  const host = hostInside(root);
  const chart = host === null ? null : charts.get(host) ?? null;
  if (host === null || chart === null || !Number.isFinite(clientX)) return null;
  const time = chart.timeScale().coordinateToTime(clientX - host.getBoundingClientRect().left);
  const seconds = typeof time === "number"
    ? time
    : typeof time === "string"
      ? Date.parse(time) / 1_000
      : time === null ? Number.NaN : Date.UTC(time.year, time.month - 1, time.day) / 1_000;
  return Number.isFinite(seconds) ? seconds * 1_000 : null;
}

/** A logical window plus one anchor bar, so a prepend can shift it back exactly (gutter included). */
export type VisibleLogicalAnchor = { from: number; to: number; anchorTime: Time; anchorIndex: number };

/**
 * 5a26f4b5: capture the owner's window as a LOGICAL range anchored on a bar that survives the
 * prepend (the newest). A time range cannot hold the gutter - its empty slots have no time.
 */
export function captureVisibleLogicalAnchor(root: ParentNode | null, anchorTime: Time): VisibleLogicalAnchor | null {
  const ts = chartInside(root)?.timeScale();
  const range = ts?.getVisibleLogicalRange() ?? null;
  const anchorIndex = ts?.timeToIndex(anchorTime, true) ?? null;
  if (range === null || anchorIndex === null) return null;
  return { from: Number(range.from), to: Number(range.to), anchorTime, anchorIndex: Number(anchorIndex) };
}

/** Restore after the child has re-fed: the same bars and the same empty slots, shifted by the prepend. */
export function restoreVisibleLogicalAnchor(root: ParentNode | null, captured: VisibleLogicalAnchor | null): boolean {
  const ts = chartInside(root)?.timeScale();
  if (!ts || captured === null) return false;
  const now = ts.timeToIndex(captured.anchorTime, true);
  if (now === null) return false;
  const next = shiftLogicalWindow(captured, Number(now));
  ts.setVisibleLogicalRange({ from: next.from, to: next.to } as never);
  return true;
}

/** Capture the exact owner-selected window before a history prepend changes the series domain. */
export function captureVisibleTimeRange(root: ParentNode | null): VisibleTimeRange | null {
  return chartInside(root)?.timeScale().getVisibleRange() ?? null;
}

/** Restore after the child has re-fed; this counters its rung-change fit without editing its owner file. */
export function restoreVisibleTimeRange(
  root: ParentNode | null,
  range: VisibleTimeRange | null,
): boolean {
  const chart = chartInside(root);
  if (chart === null || range === null) return false;
  chart.timeScale().setVisibleRange(range);
  return true;
}
